export class AcademyError extends Error {
  public readonly code: string;
  public readonly metadata?: Record<string, unknown>;

  constructor(code: string, message: string, metadata?: Record<string, unknown>) {
    super(message);
    this.name = 'AcademyError';
    this.code = code;
    this.metadata = metadata;
  }
}

export class ValidationError extends AcademyError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    super('VALIDATION_ERROR', message, metadata);
    this.name = 'ValidationError';
  }
}

export class NotFoundError extends AcademyError {
  constructor(resource: string, id: string | number) {
    super('NOT_FOUND', `${resource} not found: ${id}`, { resource, id });
    this.name = 'NotFoundError';
  }
}

/**
 * Somebody else changed this record since it was read.
 *
 * Distinct from a validation error because the caller's response is different: the edit was not
 * wrong, it was based on a version that no longer exists, and the person needs to be shown the
 * choice rather than told they made a mistake. The metadata carries what is needed to offer it.
 */
export class ConflictError extends AcademyError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    super('CONFLICT', message, metadata);
    this.name = 'ConflictError';
  }
}

export class ExternalServiceError extends AcademyError {
  constructor(service: string, message: string, metadata?: Record<string, unknown>) {
    super('EXTERNAL_SERVICE_ERROR', `${service}: ${message}`, { service, ...metadata });
    this.name = 'ExternalServiceError';
  }
}

/**
 * The model declined to answer (`stop_reason: 'refusal'`, BON-1108 O2).
 *
 * It used to fall through as an ordinary end with little or no text, so a reformat designer read it
 * as "nothing to change" and a verifier as an empty verdict. A refusal is not an answer, and it is
 * said as itself, with whatever the model wrote, so the person sees why nothing was produced.
 */
export class ModelRefusalError extends AcademyError {
  /**
   * `category` is the provider's own reason (`stop_details.category`: cyber, bio and so on), when
   * it gives one, so one refusal can be told from another without reading the prose.
   */
  constructor(agentKey: string, said: string, category?: string | null) {
    super(
      'MODEL_REFUSED',
      `The model declined this request${said ? `: ${said.slice(0, 300)}` : '.'}`,
      { agentKey, ...(category ? { category } : {}) },
    );
    this.name = 'ModelRefusalError';
  }
}

/**
 * The model's answer was cut off at the output limit (`stop_reason: 'max_tokens'`).
 *
 * Its own class rather than a ValidationError, because nothing about the request was invalid and
 * the remedy is different: ask for less in one call, or split the work (build principle 18). A
 * partial answer is never parsed as a whole one.
 */
export class TruncationError extends AcademyError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    super('MODEL_TRUNCATED', message, metadata);
    this.name = 'TruncationError';
  }
}

/**
 * The model ended without calling the tool its answer has to arrive through, after the reminders
 * the agent loop gives (`requireTool`, `continueUntil`). The 5.5 models refuse a forced tool choice,
 * so asking is the mechanism, and this is what asking failing looks like.
 */
export class ToolNotCalledError extends AcademyError {
  constructor(tool: string, reminders: number, metadata?: Record<string, unknown>) {
    super(
      'TOOL_NOT_CALLED',
      `The model ended without calling ${tool}, after ${reminders} reminders`,
      { tool, reminders, ...metadata },
    );
    this.name = 'ToolNotCalledError';
  }
}

export class CircuitOpenError extends AcademyError {
  constructor(service: string) {
    super('CIRCUIT_OPEN', `Circuit breaker open for ${service}`, { service });
    this.name = 'CircuitOpenError';
  }
}

/**
 * A missing or unusable piece of configuration — an env var that has to be set for a feature to work
 * at all.
 *
 * Distinct from `ValidationError` (the caller sent something wrong) and `ExternalServiceError` (the
 * far end failed) because the remedy is different and only an operator can apply it: nothing about
 * the request or the third party will change the outcome.
 */
export class ConfigurationError extends AcademyError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    super('CONFIGURATION_ERROR', message, metadata);
    this.name = 'ConfigurationError';
  }
}
